import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import Sprite from '../../../Prototype/Sprite';
import { Point } from 'pixi.js';
import { AwaitIterators } from '../AnimationHelper';
import {
    createPositionTween,
    getTweenProps,
    createAlphaTween,
    createScaleTween,
    createRotationTween,
} from '../AnimationTweenHelper';
import { defaultCanvasSize } from '../../../Constants/Canvas';
import { Easing } from '@tweenjs/tween.js';
import { delay } from '../../../Utility/Task/TaskQueue';

interface Meta {
    value: number;
    texture: string;
    targetPosition: Point;
}

const positions = [
    { x: -20, y: 20 },
    { x: 60, y: 80 },
    { x: -60, y: 130 },
];

export class RewardCoinsEffect extends AnimationClip {
    private targetPosition: Point;
    *execute(
        gameObject: GameObject,
        { value, texture, targetPosition }: Meta
    ): IterableIterator<number> {
        this.targetPosition = targetPosition;
        yield* AwaitIterators([
            this.createHalo(),
            this.createReward(texture, value),
        ]);
        this.gameObject.destroy();
    }

    *createReward(texture: string, value: number): IterableIterator<number> {
        const tweens: IterableIterator<number>[] = [];
        const rewardObjects: GameObject[] = [];
        for (let i = 0; i < value; i++) {
            const position = positions[i];
            const reward = GameObject.Instantiate(Sprite, {
                texture,
                position: { ...position, y: position.y + 20 },
                anchor: new Point(0.5, 0.5),
                alpha: 0,
            });

            rewardObjects.push(reward);

            this.gameObject.addChild(reward);

            const positionTween = createPositionTween({
                ...getTweenProps(
                    reward,
                    this,
                    new Point(position.x, position.y - 20)
                ),
                duration: 250 * i,
            });

            const alphaTween = createAlphaTween({
                ...getTweenProps(reward, this, { alpha: 1 }),
                duration: 250 * i,
            });

            tweens.push(
                ...[this.startTween(positionTween), this.startTween(alphaTween)]
            );
        }

        yield* AwaitIterators(tweens);
        yield* this.goToTarget(rewardObjects);
    }

    *createHalo() {
        const flair = GameObject.Instantiate(Sprite, {
            texture: 'flair',
            anchor: new Point(0.5, 0.5),
            alpha: 1,
        });
        const halo = GameObject.Instantiate(Sprite, {
            texture: 'yeallow_halo',
            anchor: new Point(0.5, 0.5),
        });

        const rotationTween = createRotationTween({
            ...getTweenProps(flair, this, 3),
            duration: 1000,
            easing: Easing.Linear.None,
        });

        const alphaTween = createAlphaTween({
            ...getTweenProps(flair, this, { alpha: 0 }),
            duration: 1000,
        });

        flair.addChild(halo);
        this.gameObject.addChild(flair);

        return yield* AwaitIterators([
            this.startTween(rotationTween),
            this.startTween(alphaTween),
        ]);
    }

    *goToTarget(objects: GameObject[]) {
        const tweens: IterableIterator<number>[] = [];
        const aimPosition = new Point(
            -defaultCanvasSize.x / 2 + this.targetPosition.x - 100,
            -defaultCanvasSize.y / 2 + this.targetPosition.y
        );
        objects.forEach((reward, index) => {
            const toTargetTween = createPositionTween({
                ...getTweenProps(reward, this, aimPosition),
                duration: 1000,
                delay: 100 * index,
            });

            const scaleTween = createScaleTween({
                ...getTweenProps(reward, this, new Point(0.6, 0.6)),
                duration: 1000,
                delay: 100 * index,
            });

            const alphaTween = createAlphaTween({
                ...getTweenProps(reward, this, { alpha: 0 }),
                duration: 500,
                delay: 100 * index + 500,
            });

            tweens.push(
                ...[
                    this.startTween(toTargetTween),
                    this.startTween(scaleTween),
                    this.startTween(alphaTween),
                    this.blast(aimPosition, 100 * index + 500),
                ]
            );
        });
        yield* AwaitIterators(tweens);

        objects.forEach(element => {
            element.destroy();
        });
    }

    *blast(position: Point, waitTime: number) {
        yield* delay(waitTime);
        const halo = GameObject.Instantiate(Sprite, {
            position,
            texture: 'yeallow_halo',
            anchor: new Point(0.5, 0.5),
            scale: new Point(0, 0),
            alpha: 0.5,
        });

        const greenHaloScaleTween = createScaleTween({
            ...getTweenProps(halo, this, new Point(1, 1)),
            duration: 800,
        });

        const greenHaloAlphaTween = createAlphaTween({
            ...getTweenProps(halo, this, { alpha: 0 }),
            duration: 800,
            delay: 200,
        });

        const haloNodeRing = GameObject.Instantiate(Sprite, {
            texture: 'halo-node-ring',
            anchor: new Point(0.5, 0.5),
            scale: new Point(0, 0),
        });

        halo.addChild(haloNodeRing);

        const haloRingScaleTween = createScaleTween({
            ...getTweenProps(haloNodeRing, this, new Point(1.2, 1.2)),
            duration: 700,
            delay: 100,
        });

        this.gameObject.addChild(halo);

        yield* AwaitIterators([
            this.startTween(greenHaloScaleTween),
            this.startTween(greenHaloAlphaTween),
            this.startTween(haloRingScaleTween),
        ]);
    }
}
